/**
 * ABC Wealth → Nomsa · Proposal Notification Showcase
 *
 * A split-screen demo:
 *   • Left  — ABC Wealth institution portal with an adviser composer.
 *             James Smith is drafting a proposal for Nomsa. One button
 *             sends it.
 *   • Right — Nomsa's phone with the Altara wallet home visible.
 *             When the proposal lands, the phone chimes, a toast slides
 *             in, and the "New decision · Trust check needed" nudge
 *             animates onto the home screen.
 *
 * All server-side wiring reuses the existing
 *   POST /api/admin/decisions/demo-send
 * which drops a real TRUST_PENDING decision into Nomsa's device.
 * Follow-up flow lives at /wallet/decisions/{id}.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, User, Send, Sparkles, Gavel, ShieldCheck, ArrowRight,
  Check, Loader2, RotateCcw, Bell, Wifi, Battery,
  FileText, ExternalLink, BadgeCheck,
} from "lucide-react";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const NOMSA_DEMO_DEVICE = "demo_nomsa_showcase_device";

const DEFAULT_PROPOSAL = {
  proposal_name: "ABC Wealth Balanced Growth Portfolio",
  financial_commitment_zar: 450_000,
  investment_term_months: 60,
  expected_return_pct: 11.5,
};

export default function WalletAbcWealthProposalDemo() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const autoplay = params.get("autoplay") === "1";

  if (embed) {
    return (
      <div
        className="w-screen h-screen flex items-center justify-center px-8 py-8 overflow-hidden"
        style={{ background: "linear-gradient(160deg, #F5EFE6 0%, #EEF2F7 55%, #E2E8F0 100%)" }}
        data-testid="abc-proposal-embed"
      >
        <div className="w-full max-w-6xl">
          <EmbedHeader />
          <div className="mt-6">
            <SplitScreen autoplay={autoplay} />
          </div>
        </div>
        <SlideBrandMark />
      </div>
    );
  }

  return (
    <WalletShell activeTab="demos">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-6 lg:py-8 space-y-6" data-testid="abc-proposal-demo">
        <Header />
        <DownloadStrip />
        <SplitScreen />
        <Footnote />
      </div>
    </WalletShell>
  );
}

function EmbedHeader() {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: GOLD }}>
        Altara Consumer Intelligence
      </div>
      <h1 className="font-display text-3xl lg:text-4xl font-semibold mt-1" style={{ color: NAVY }}>
        ABC Wealth sends a proposal to Nomsa
      </h1>
      <div className="text-sm text-slate-600 mt-1.5">
        Governed delivery — one tap into her Trust Wallet.
      </div>
    </div>
  );
}

function SlideBrandMark() {
  return (
    <div className="absolute bottom-4 right-6 flex items-center gap-2 text-[10px] tracking-widest uppercase"
         style={{ color: NAVY, opacity: 0.55 }} data-testid="slide-brand">
      <div className="w-4 h-4 rounded-md flex items-center justify-center" style={{ background: NAVY }}>
        <span className="font-display font-bold text-[9px]" style={{ color: GOLD }}>A</span>
      </div>
      altara.co
    </div>
  );
}

function DownloadStrip() {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3"
         data-testid="abc-proposal-downloads">
      <div className="flex-1 min-w-[220px]">
        <div className="text-[10px] tracking-[0.24em] uppercase font-semibold" style={{ color: GOLD }}>
          Slide assets
        </div>
        <div className="text-sm text-slate-700 mt-0.5">
          Drop-in animation for a PowerPoint deck — MP4 or animated GIF.
        </div>
      </div>
      <a
        href="/abc-wealth-proposal.mp4"
        download
        data-testid="download-mp4"
        className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold text-white"
        style={{ background: NAVY }}
      >
        Download MP4 <ArrowRight className="w-3.5 h-3.5" />
      </a>
      <a
        href="/abc-wealth-proposal.gif"
        download
        data-testid="download-gif"
        className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold border"
        style={{ background: "white", borderColor: NAVY, color: NAVY }}
      >
        Download GIF <ArrowRight className="w-3.5 h-3.5" />
      </a>
      <Link
        to="/wallet/demos/abc-wealth-proposal?embed=1"
        target="_blank"
        data-testid="open-embed"
        className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[12px] font-semibold"
        style={{ background: `${GOLD}22`, color: NAVY }}
      >
        Open embed view <ExternalLink className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

function Header() {
  return (
    <header data-testid="abc-proposal-header">
      <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Demo Simulation</div>
      <h1 className="font-display text-2xl lg:text-3xl font-semibold" style={{ color: NAVY }}>
        ABC Wealth sends a proposal to Nomsa
      </h1>
      <p className="text-sm text-slate-600 mt-2 max-w-3xl">
        A participating institution drafts an investment proposal in their own
        console. One tap delivers it into Nomsa&apos;s Altara wallet as a
        governed <strong className="text-slate-800">Trust Check</strong> — not
        a broadcast email or an SMS link.
      </p>
    </header>
  );
}

/* ══════════════════════════════════════════════════════════════
   Split screen
   ══════════════════════════════════════════════════════════════ */

function SplitScreen({ autoplay = false }) {
  const [status, setStatus] = useState("idle"); // idle | sending | landed
  const [error, setError] = useState("");
  const [decision, setDecision] = useState(null);
  const [proposal, setProposal] = useState(DEFAULT_PROPOSAL);
  const [phoneToast, setPhoneToast] = useState(false);
  const [phoneTapping, setPhoneTapping] = useState(false);
  const [phoneOpened, setPhoneOpened] = useState(false);
  const [phoneNudge, setPhoneNudge] = useState(false);
  const [flying, setFlying] = useState(false);
  const autoplayFired = useRef(false);

  const send = async () => {
    if (status === "sending") return;
    setError(""); setStatus("sending"); setFlying(true);
    try {
      // Reset any prior demo decisions so the state is deterministic
      await axios.post(`${API}/admin/decisions/demo-reset`, { device_id: NOMSA_DEMO_DEVICE }).catch(() => {});
      const { data } = await axios.post(`${API}/admin/decisions/demo-send`, {
        device_id: NOMSA_DEMO_DEVICE,
        financial_commitment_zar: proposal.financial_commitment_zar,
        investment_term_months: proposal.investment_term_months,
      });
      // Dramatised delivery — envelope in flight for ~1.4s, then toast, then nudge, then tap-to-open
      await sleep(1400);
      setFlying(false);
      setDecision(data);
      setPhoneToast(true);
      await sleep(650);
      setPhoneNudge(true);
      setStatus("landed");
      // After the toast, Nomsa taps the notification — the tap ripple appears — then the wallet opens the proposal
      await sleep(2200);
      setPhoneTapping(true);
      await sleep(700);
      setPhoneToast(false);
      setPhoneOpened(true);
      setPhoneTapping(false);
    } catch (e) {
      setStatus("idle");
      setFlying(false);
      setError(e?.response?.data?.detail || e?.message || "Send failed");
    }
  };

  const reset = () => {
    setStatus("idle");
    setDecision(null);
    setPhoneToast(false);
    setPhoneTapping(false);
    setPhoneOpened(false);
    setPhoneNudge(false);
    setFlying(false);
    setError("");
  };

  // Autoplay hook — used by the recording script and embed view
  useEffect(() => {
    if (!autoplay || autoplayFired.current) return;
    autoplayFired.current = true;
    const t = setTimeout(() => { send(); }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay]);

  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
      <InstitutionPanel
        status={status}
        error={error}
        proposal={proposal}
        onProposalChange={setProposal}
        onSend={send}
        onReset={reset}
      />
      <ConsumerPanel
        status={status}
        flying={flying}
        toast={phoneToast}
        tapping={phoneTapping}
        opened={phoneOpened}
        nudge={phoneNudge}
        decision={decision}
        proposal={proposal}
      />
    </section>
  );
}

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

/* ── LEFT · ABC Wealth adviser console mockup ─────────────────── */

function InstitutionPanel({ status, error, proposal, onProposalChange, onSend, onReset }) {
  const sent = status === "landed";
  const busy = status === "sending";
  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden flex flex-col"
         data-testid="institution-panel">
      {/* Chrome */}
      <div className="px-5 py-4 flex items-center justify-between" style={{ background: NAVY }}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
               style={{ background: GOLD }}>
            <Building2 className="w-4 h-4" style={{ color: NAVY }} />
          </div>
          <div>
            <div className="text-[9px] tracking-[0.28em] uppercase" style={{ color: GOLD }}>Institution portal</div>
            <div className="text-[14px] font-semibold text-white">ABC Wealth · Adviser Console</div>
          </div>
        </div>
        <span className="text-[10px] tracking-widest px-2 py-1 rounded-full font-semibold"
              style={{ background: `${GOLD}33`, color: GOLD }}>PARTICIPATING</span>
      </div>

      {/* Adviser context */}
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
        <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
             style={{ background: `linear-gradient(135deg, ${GOLD} 0%, #F5EFE6 100%)` }}>
          <User className="w-5 h-5" style={{ color: NAVY }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold" style={{ color: NAVY }}>James Smith</div>
          <div className="text-[11px] text-slate-500">Senior Financial Adviser · FAIS Rep #A-2214-JS</div>
        </div>
        <BadgeCheck className="w-4 h-4" style={{ color: GOLD }} title="FSCA verified" />
      </div>

      {/* Composer */}
      <div className="p-5 space-y-4 flex-1">
        <div className="text-[10px] tracking-[0.24em] uppercase font-semibold text-slate-500">
          New proposal for
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center"
               style={{ background: `${NAVY}12`, color: NAVY }}>
            <User className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold" style={{ color: NAVY }}>Nomsa</div>
            <div className="text-[11px] text-slate-500">Client · relationship established 2021 · Altara-linked</div>
          </div>
          <span className="text-[9px] tracking-widest px-2 py-0.5 rounded font-semibold"
                style={{ background: "#ECFDF5", color: "#065F46" }}>WALLET-READY</span>
        </div>

        <ComposerField
          label="Proposal name"
          value={proposal.proposal_name}
          onChange={v => onProposalChange({ ...proposal, proposal_name: v })}
          disabled={busy || sent}
          testId="proposal-name"
        />
        <div className="grid grid-cols-2 gap-3">
          <ComposerField
            label="Contribution (ZAR)"
            value={proposal.financial_commitment_zar}
            type="number"
            onChange={v => onProposalChange({ ...proposal, financial_commitment_zar: Number(v) })}
            disabled={busy || sent}
            testId="proposal-amount"
          />
          <ComposerField
            label="Term (months)"
            value={proposal.investment_term_months}
            type="number"
            onChange={v => onProposalChange({ ...proposal, investment_term_months: Number(v) })}
            disabled={busy || sent}
            testId="proposal-term"
          />
        </div>

        {/* Provenance strip */}
        <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3 space-y-1.5">
          <ProvRow label="Trust anchor" value="Altara Trust Utility · KY-I registry" />
          <ProvRow label="Institution reg" value="FSCA FSP #48291" />
          <ProvRow label="Delivery channel" value="Altara wallet (governed)" />
        </div>

        {error && (
          <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2.5"
               data-testid="send-error">{error}</div>
        )}
      </div>

      {/* Footer action */}
      <div className="px-5 pb-5 pt-1 flex items-center gap-2">
        {!sent ? (
          <button
            onClick={onSend}
            disabled={busy}
            data-testid="send-proposal-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white transition disabled:opacity-60"
            style={{ background: NAVY }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {busy ? "Delivering to Nomsa's wallet…" : "Send proposal to Nomsa's wallet"}
          </button>
        ) : (
          <>
            <div className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold"
                 style={{ background: "#ECFDF5", color: "#065F46", border: "1px solid #6EE7B7" }}
                 data-testid="send-success">
              <Check className="w-4 h-4" /> Delivered · Trust Check pending
            </div>
            <button
              onClick={onReset}
              data-testid="send-reset-btn"
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-3 text-sm font-medium border"
              style={{ background: "white", borderColor: "#E2E8F0", color: NAVY }}
              title="Reset demo"
            ><RotateCcw className="w-4 h-4" /></button>
          </>
        )}
      </div>
    </div>
  );
}

function ComposerField({ label, value, onChange, type = "text", disabled, testId }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.22em] uppercase text-slate-500 font-semibold">{label}</label>
      <input
        value={value}
        type={type}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-slate-400 disabled:bg-slate-50 disabled:text-slate-600"
      />
    </div>
  );
}

function ProvRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-[11px]">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-700">{value}</span>
    </div>
  );
}

/* ── RIGHT · Nomsa's phone with wallet home ───────────────────── */

function ConsumerPanel({ status, flying, toast, tapping, opened, nudge, decision, proposal }) {
  return (
    <div className="relative rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col p-6 items-center justify-center"
         style={{ background: "linear-gradient(160deg, #EEF2F7 0%, #E2E8F0 100%)" }}
         data-testid="consumer-panel">
      {/* Header strip */}
      <div className="absolute top-0 left-0 right-0 px-5 py-3 flex items-center justify-between border-b border-slate-200/80 bg-white/70 backdrop-blur">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: NAVY }}>
            <span className="font-display font-bold text-[10px]" style={{ color: GOLD }}>A</span>
          </div>
          <span className="text-[11px] font-semibold" style={{ color: NAVY }}>Nomsa&apos;s device</span>
        </div>
        <span className="text-[10px] tracking-widest uppercase text-slate-500">Live</span>
      </div>

      {/* Flying envelope */}
      <AnimatePresence>
        {flying && (
          <motion.div
            key="envelope"
            initial={{ x: "-45%", y: 0, scale: 0.6, opacity: 0 }}
            animate={{ x: 0, y: 0, scale: 1, opacity: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{ duration: 1.3, ease: "easeInOut" }}
            className="absolute z-20 top-1/2 left-0 -translate-y-1/2 w-14 h-14 rounded-2xl flex items-center justify-center shadow-2xl"
            style={{ background: NAVY }}
            data-testid="flying-envelope"
          >
            <Send className="w-6 h-6" style={{ color: GOLD }} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Phone frame */}
      <div className="mt-8" data-testid="nomsa-phone">
        <NomsaPhone toast={toast} tapping={tapping} opened={opened} nudge={nudge} decision={decision} proposal={proposal} status={status} />
      </div>

      {/* Open-in-wallet CTA once landed */}
      {status === "landed" && decision && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-5"
        >
          <Link
            to={`/wallet/decisions/${decision.decision_id}`}
            data-testid="open-decision-link"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-semibold text-white shadow-sm"
            style={{ background: NAVY }}
          >
            Open in Nomsa&apos;s wallet <ExternalLink className="w-3.5 h-3.5" />
          </Link>
        </motion.div>
      )}
    </div>
  );
}

/* Phone-frame + wallet home mockup with the arriving nudge */
function NomsaPhone({ toast, tapping, opened, nudge, decision, proposal, status }) {
  return (
    <div
      className="relative"
      style={{
        width: 300,
        height: 600,
        borderRadius: 42,
        padding: 11,
        background: "linear-gradient(145deg, #111 0%, #1a1a1a 100%)",
        boxShadow: "0 30px 70px -20px rgba(0,0,0,0.6), 0 0 0 2px rgba(255,255,255,0.05) inset",
      }}
    >
      {/* Notch */}
      <div className="absolute top-3 left-1/2 -translate-x-1/2 rounded-b-2xl z-30"
           style={{ width: 100, height: 20, background: "#0a0a0a" }} />

      {/* Screen */}
      <div className="relative w-full h-full rounded-[30px] overflow-hidden"
           style={{ background: "#F5EFE6", color: NAVY }}>
        {/* Status bar */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2 text-[10px] font-semibold"
             style={{ color: NAVY }}>
          <span>9:41</span>
          <div className="flex items-center gap-1">
            <Wifi className="w-3 h-3" />
            <Battery className="w-3.5 h-3.5" />
          </div>
        </div>

        {/* Toast — arrives when notification lands */}
        <AnimatePresence>
          {toast && (
            <motion.div
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -30, opacity: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
              className="absolute top-9 left-3 right-3 z-20 rounded-xl shadow-lg p-3 flex items-start gap-2"
              style={{ background: NAVY, color: "white" }}
              data-testid="phone-toast"
            >
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                   style={{ background: GOLD, color: NAVY }}>
                <Bell className="w-3.5 h-3.5" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] tracking-widest uppercase" style={{ color: GOLD }}>Altara · now</div>
                <div className="text-[12px] font-semibold leading-tight">New proposal from ABC Wealth</div>
                <div className="text-[10px] mt-0.5" style={{ color: "rgba(255,255,255,0.7)" }}>
                  Tap to run a Trust Check
                </div>
              </div>
              {/* Tap ripple + fingertip — appears when Nomsa taps the notification */}
              {tapping && (
                <>
                  <motion.div
                    className="absolute inset-0 rounded-xl pointer-events-none"
                    style={{ border: `2px solid ${GOLD}` }}
                    initial={{ opacity: 0.9, scale: 1 }}
                    animate={{ opacity: 0, scale: 1.4 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                  />
                  <motion.div
                    className="absolute -bottom-4 right-6 pointer-events-none z-30"
                    initial={{ opacity: 0, y: 20, scale: 1.4 }}
                    animate={{ opacity: [0, 1, 1, 0], y: [20, 0, 0, -8], scale: [1.4, 1, 1, 0.9] }}
                    transition={{ duration: 0.7, times: [0, 0.3, 0.8, 1] }}
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center shadow-lg"
                         style={{ background: "rgba(255,255,255,0.95)", border: `2px solid ${GOLD}` }}>
                      <div className="w-3.5 h-3.5 rounded-full" style={{ background: GOLD }} />
                    </div>
                  </motion.div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Opened proposal — takes over the phone screen after Nomsa taps the notification */}
        <AnimatePresence>
          {opened && (
            <motion.div
              key="opened"
              initial={{ y: 20, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-8 left-0 right-0 bottom-0 z-25 px-3 pt-4"
              style={{ background: "linear-gradient(180deg, #F5EFE6 0%, #FFF6E0 100%)" }}
              data-testid="phone-opened-proposal"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: NAVY }}>
                  <span className="font-display font-bold text-[13px]" style={{ color: GOLD }}>A</span>
                </div>
                <div>
                  <div className="text-[8px] tracking-[0.25em] uppercase" style={{ color: NAVY, opacity: 0.55 }}>Altara · Trust Wallet</div>
                  <div className="text-[10px] font-semibold" style={{ color: NAVY }}>Investment proposal · v1.0</div>
                </div>
              </div>
              <div className="rounded-xl border p-3" style={{ borderColor: "rgba(10,31,68,0.15)", background: "white" }}>
                <div className="text-[9px] uppercase tracking-widest" style={{ color: GOLD }}>Investment proposal</div>
                <div className="text-[14px] font-bold leading-tight mt-1" style={{ color: NAVY }}>ABC Wealth Balanced Growth Portfolio</div>
                <div className="text-[9px] mt-1" style={{ color: NAVY, opacity: 0.65 }}>Sent by ABC Wealth · via James Smith</div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div>
                    <div className="text-[8px] uppercase" style={{ color: NAVY, opacity: 0.55 }}>Commitment</div>
                    <div className="text-[12px] font-bold" style={{ color: NAVY }}>R {(proposal?.financial_commitment_zar ?? 450000).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[8px] uppercase" style={{ color: NAVY, opacity: 0.55 }}>Term</div>
                    <div className="text-[12px] font-bold" style={{ color: NAVY }}>{proposal?.investment_term_months ?? 60} months</div>
                  </div>
                </div>
                <motion.div
                  className="mt-3 rounded-lg p-2 flex items-center gap-2"
                  style={{ background: NAVY }}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35, duration: 0.4 }}
                >
                  <ShieldCheck className="w-3.5 h-3.5" style={{ color: GOLD }} />
                  <div className="text-[9px] font-semibold text-white">Running Trust Check…</div>
                </motion.div>
              </div>
              <div className="mt-3 flex items-center gap-1.5">
                <motion.div
                  className="rounded-full flex-1 h-1 overflow-hidden"
                  style={{ background: "rgba(10,31,68,0.1)" }}
                >
                  <motion.div
                    className="h-full"
                    style={{ background: GOLD }}
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 1.6, ease: "easeInOut" }}
                  />
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Wallet header */}
        <div className="px-4 pt-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: NAVY }}>
              <span className="font-display font-bold text-[13px]" style={{ color: GOLD }}>A</span>
            </div>
            <div>
              <div className="text-[8px] tracking-[0.25em] uppercase" style={{ color: NAVY, opacity: 0.55 }}>Altara</div>
              <div className="text-[11px] font-semibold leading-tight">Trust Wallet</div>
            </div>
          </div>
          <div className="relative">
            <Bell className="w-4 h-4" style={{ color: NAVY }} />
            {nudge && (
              <motion.span
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full"
                style={{ background: GOLD, border: "1.5px solid #F5EFE6" }}
                data-testid="phone-bell-dot"
              />
            )}
          </div>
        </div>

        {/* Greeting */}
        <div className="px-4 mt-3">
          <div className="text-[9px] tracking-[0.2em] uppercase" style={{ color: NAVY, opacity: 0.5 }}>Good morning</div>
          <div className="font-display text-xl font-semibold" style={{ color: NAVY }}>Nomsa</div>
        </div>

        {/* Content area */}
        <div className="px-3 mt-3 space-y-2">
          {/* The arriving nudge (state B) or placeholder (state A) */}
          <AnimatePresence mode="wait">
            {nudge && decision ? (
              <motion.div
                key="nudge"
                initial={{ opacity: 0, y: 15, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.55, ease: "easeOut" }}
                className="rounded-xl p-3 border shadow-sm"
                style={{
                  background: "linear-gradient(135deg, #FFFDF6 0%, #FFF6E0 100%)",
                  borderColor: "#F0D689",
                }}
                data-testid="phone-nudge"
              >
                <div className="flex items-start gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                       style={{ background: NAVY }}>
                    <Gavel className="w-3.5 h-3.5" style={{ color: GOLD }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[8.5px] tracking-[0.24em] uppercase font-semibold"
                         style={{ color: GOLD }}>
                      New decision · Trust check needed
                    </div>
                    <div className="text-[12px] font-semibold leading-tight mt-0.5" style={{ color: NAVY }}>
                      ABC Wealth sent you an investment proposal
                    </div>
                    <div className="text-[10px] text-slate-600 mt-1 leading-snug">
                      <strong style={{ color: NAVY }}>{decision.proposal_name}</strong> · R{formatZAR(proposal.financial_commitment_zar)} over {proposal.investment_term_months} months.
                    </div>
                    <div className="mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold text-white"
                         style={{ background: NAVY }}>
                      Open Trust Check <ArrowRight className="w-2.5 h-2.5" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="placeholder"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="rounded-xl p-3 border border-dashed"
                style={{ borderColor: "rgba(10,31,68,0.15)", background: "rgba(255,255,255,0.5)" }}
              >
                <div className="text-[10px] tracking-widest uppercase" style={{ color: NAVY, opacity: 0.55 }}>
                  Inbox · quiet
                </div>
                <div className="text-[12px] mt-1 text-slate-500 leading-snug">
                  Nothing needs your attention right now.
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Static quick tiles */}
          <div className="grid grid-cols-2 gap-2">
            <PhoneTile Icon={Gavel} label="Decisions" note={nudge ? "1 new" : "—"} pulse={nudge} />
            <PhoneTile Icon={ShieldCheck} label="Institutions" note="ABC Wealth" />
            <PhoneTile Icon={FileText} label="Documents" note="8 on file" />
            <PhoneTile Icon={Sparkles} label="Ask NAVI" note="Anytime" />
          </div>
        </div>

        {/* Bottom nav */}
        <div className="absolute left-0 right-0 bottom-0 px-4 py-2.5 flex items-center justify-around border-t"
             style={{ background: "white", borderColor: `${NAVY}0F` }}>
          <PhoneNav Icon={ShieldCheck} label="Home" active />
          <PhoneNav Icon={FileText} label="Docs" />
          <PhoneNav Icon={Sparkles} label="NAVI" />
          <PhoneNav Icon={Gavel} label="Decide" badge={nudge} />
        </div>
      </div>
    </div>
  );
}

function PhoneTile({ Icon, label, note, pulse }) {
  return (
    <div
      className="rounded-lg p-2 border relative overflow-hidden"
      style={{
        background: "white",
        borderColor: pulse ? `${GOLD}` : `${NAVY}10`,
        boxShadow: pulse ? `0 0 0 2px ${GOLD}33` : "none",
      }}
    >
      {pulse && (
        <motion.div
          initial={{ opacity: 0.6 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 1.4, repeat: Infinity }}
          className="absolute inset-0 pointer-events-none"
          style={{ background: `${GOLD}22` }}
        />
      )}
      <div className="flex items-center gap-1.5 relative">
        <Icon className="w-3 h-3" style={{ color: NAVY }} />
        <div className="text-[9px] font-semibold tracking-wide" style={{ color: NAVY }}>{label}</div>
      </div>
      <div className="text-[9px] mt-0.5 relative" style={{ color: NAVY, opacity: 0.55 }}>{note}</div>
    </div>
  );
}

function PhoneNav({ Icon, label, active, badge }) {
  return (
    <div className="flex flex-col items-center gap-0.5 relative"
         style={{ color: active ? NAVY : `${NAVY}66` }}>
      <div className="relative">
        <Icon className="w-4 h-4" />
        {badge && (
          <span className="absolute -top-0.5 -right-1 w-1.5 h-1.5 rounded-full"
                style={{ background: GOLD }} />
        )}
      </div>
      <span className="text-[8px] font-medium">{label}</span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */

function Footnote() {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-4 flex items-start gap-3"
         data-testid="abc-proposal-footnote">
      <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
      <div className="text-[12px] text-slate-600 leading-relaxed">
        <strong className="text-slate-800">How this works.</strong> This showcase
        calls the real <code className="text-[11px] px-1 py-0.5 rounded bg-white border border-slate-200">POST /api/admin/decisions/demo-send</code> endpoint, which drops a
        proper TRUST_PENDING decision into Nomsa&apos;s device. The notification
        is not simulated — clicking &ldquo;Open in Nomsa&apos;s wallet&rdquo; takes you into
        the actual 4-stage journey (TRUST → UNDERSTAND → CHECK → ACT).
      </div>
    </div>
  );
}

function formatZAR(n) {
  try { return Number(n).toLocaleString("en-ZA"); } catch { return n; }
}
